import React, { useContext, useState } from 'react';
import { LocaleContext } from '../../../components/LocaleContext';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import { postRequest } from '../../../api/ApiController';
import UtilContext from '../../../components/UtilContext';
import { Dropdown } from 'primereact/dropdown';
import { SEMAFOR_COLOR_OPTIONS, SEMAFOR_COLOR_VALUES, getSemaforColorClass } from '../../../utils/moduleSettings';

/** Hodnota z DB môže byť názov farby alebo starý hex – pre Dropdown vrátime platný názov alebo defaultName. */
function effectiveColorValue(value, defaultName) {
  const v = String(value || '').trim();
  if (SEMAFOR_COLOR_VALUES.includes(v)) return v;
  return defaultName;
}

function ColorSelectRow({ label, setting, defaultName, dispatch, toast, t }) {
  const [saving, setSaving] = useState(false);
  const currentValue = effectiveColorValue(setting?.value, defaultName);

  async function save(newVal) {
    if (!setting) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 5000, detail: t('Common.Toast.SettingNotFoundDetail') });
      return;
    }
    if (newVal === currentValue) return;
    setSaving(true);
    try {
      await postRequest(`/api/module_settings/update/${setting.id}`, { value: newVal }, true);
      dispatch({ type: 'update_module_setting', id: setting.id, value: newVal });
      toast?.current?.show({ severity: 'info', summary: t('Common.Saved'), life: 2000, detail: t('Common.Toast.ColorChangedDetail') });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 3000, detail: e?.message || t('Common.Toast.SaveFailedDetail') });
    } finally {
      setSaving(false);
    }
  }

  if (!setting) return null;
  const colorClass = getSemaforColorClass(currentValue);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[minmax(200px,1fr)_220px] gap-3 items-center py-1.5">
      <label className="text-sm font-medium text-gray-800 truncate">{label}</label>
      <div className="flex items-center gap-2 shrink-0 min-w-0">
        <span className={`inline-block w-5 h-5 rounded-full shrink-0 ${colorClass}`} title={currentValue} aria-hidden />
        <Dropdown
          value={currentValue}
          options={SEMAFOR_COLOR_OPTIONS}
          optionLabel="label"
          optionValue="value"
          onChange={(e) => save(e.value)}
          className="w-[180px] border border-gray-400 rounded"
          placeholder={t('ChecklistGeneralSettings.ColorPlaceholder')}
          disabled={saving}
        />
      </div>
    </div>
  );
}

export default function ChecklistGeneralSettings() {
  const { t } = useContext(LocaleContext);
  const [state, dispatch] = useContext(DatabaseContext);
  const { toast } = useContext(UtilContext);
  const module_settings = state.module_settings || [];
  const getSettingByKey = (key) => module_settings.find(s => s.module === 'checklists' && s.key === key);

  const statusLabels = ['OK', 'NOK', t('Status.InProgressWork'), t('Status.NotCreated')];
  const statusDefaultNames = ['green', 'red', 'orange', 'gray'];
  const vyjadreniaLabels = [t('Status.OpenPlural'), t('Common.OpenOverdue'), t('Status.ClosedPlural')];
  const vyjadreniaDefaultNames = ['green', 'red', 'gray'];

  return (
    <div className="relative flex flex-col h-[calc(100vh-137px)] bg-gray-100 overflow-y-auto overflow-x-hidden">
      <div className="p-4 w-full">
        <h1 className="text-3xl font-semibold text-left text-zinc-900 mb-4">
          {t('ChecklistGeneralSettings.Title')}
        </h1>
        <div className="p-6 bg-white rounded-md shadow-md w-full max-w-3xl">
          <section>
            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200">{t('Ui.R67.Farby_grafov_nahlad_checklistov')}</h2>
            <p className="text-sm text-gray-500 mb-5">{t('Ui.R67.Farby_v_grafoch_na_PC_aj_v_mobile_sa_nacitavaju_z_tychto_nas')}</p>

            <div className="mb-6">
              <h3 className="text-base font-semibold text-zinc-700 mb-2">{t('Ui.R67.Graf_stavov_OK_NOK_Rozpracovany_Nevytvoreny')}</h3>
              <div className="pl-4 flex flex-col gap-1 border-l-2 border-gray-200">
                {[1, 2, 3, 4].map((i) => (
                  <ColorSelectRow
                    key={`graph_status_${i}`}
                    label={statusLabels[i - 1]}
                    setting={getSettingByKey(`graph_status_${i}`)}
                    defaultName={statusDefaultNames[i - 1]}
                    dispatch={dispatch}
                    toast={toast}
                    t={t}
                  />
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-base font-semibold text-zinc-700 mb-2">{t('Ui.R67.Graf_vyjadreni_Otvorene_Otvorene_po_termine_Uzavrete')}</h3>
              <div className="pl-4 flex flex-col gap-1 border-l-2 border-gray-200">
                {[1, 2, 3].map((i) => (
                  <ColorSelectRow
                    key={`graph_vyjadrenia_${i}`}
                    label={vyjadreniaLabels[i - 1]}
                    setting={getSettingByKey(`graph_vyjadrenia_${i}`)}
                    defaultName={vyjadreniaDefaultNames[i - 1]}
                    dispatch={dispatch}
                    toast={toast}
                    t={t}
                  />
                ))}
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
